import { Stack } from "@mui/material";
import TextAttachment from "components/attachments/TextAttachment";
import URLSelector from "components/attachments/URLSelector";
import ActionButton from "components/react-lib/generic-ui/ActionButton";
import AlertMessage from "components/react-lib/generic-ui/AlertMessage";
import FileUploader from "components/react-lib/generic-ui/FileUploader";
import ImageUploader from "components/react-lib/generic-ui/ImageUploader";
import Selector from "components/react-lib/generic-ui/Selector";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import { addDoc, collection } from "firebase/firestore";
import { db } from "firebaseApp";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { popupActions } from "store/react-lib/generic-ui/popup";
import YoutubeTranscript from "./YoutubeTranscript";


export const URL = { id: "url", label: "URL" };
export const FILE = { id: "file", label: "File" };
export const IMAGE = { id: "image", label: "Image" };
export const TEXT = { id: "text", label: "Text" };
export const YT = { id: "youtube", label: "YouTube transcript" };

export default function UploadFileForm({
  successCallback = ({fileId, fileData}) => {},
}) {

  const auth = useAuth();
  const dispatch = useDispatch();
  const supportedTypes = [URL, FILE, IMAGE, TEXT];

  const [attachmentType, setAttachmentType] = useState(supportedTypes[0].id);
  const [attachment, setAttachment] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleFileUploaded = (file) => {
    setAttachment(file);
  };

  const renderForm = () => {
    switch (attachmentType) {
      case YT.id:
        return <YoutubeTranscript value={attachment} setValue={setAttachment} />;
      case TEXT.id:
        return <TextAttachment value={attachment} setValue={setAttachment} />;
      case URL.id:
        return <URLSelector value={attachment} setValue={setAttachment} />;
      case FILE.id:
        return <FileUploader successCallback={handleFileUploaded} />;
      case IMAGE.id:
        return (
          <ImageUploader
            successCallback={handleImageUploaded}
            bucketName="inputImages"
            preview={true}
          />
        );
    }
    console.warn(`Invalid attachment type ${attachmentType}`);
    return null;
  };

  const handleImageUploaded = (image) => {
    setAttachment(image);
  };

  const canUpload = () => {
    switch (attachmentType) {
      case YT.id:
        return Boolean(attachment?.content);
      case URL.id:
        return Boolean(attachment?.content) && Boolean(attachment?.name);
      case FILE.id:
        return Boolean(attachment?.filename);
      case IMAGE.id:
        return Boolean(attachment?.imageUrl);
      case TEXT.id:
        return Boolean(attachment?.content) && Boolean(attachment?.name);
    }
    return false;
  };

  const handleUpload = async () => {
    try {
      setProcessing(true);
      console.log(attachment);
      const fileData =  {
        ...attachment,
        uid: auth.currentUser.uid,
        type: attachmentType,
        createdAt: new Date().getTime(),
      };
      const fileRef = await addDoc(collection(db, "files"), fileData);
      reset();
      successCallback({
        fileId: fileRef.id,
        fileData,
      });
    } catch (error) {
      console.log(error);
      dispatch(popupActions.showError("Couldn't upload. Try again later."))
    } finally {
      setProcessing(false);
    }
  }

  const reset = () => {
    setAttachmentType(supportedTypes[0].id);
    setAttachment(null);
    setProcessing(false);
  };

  return (
    <Stack mt={1} spacing={1}>
      <Selector
        label="Upload type"
        value={attachmentType}
        setValue={setAttachmentType}
        options={supportedTypes}
        valueField="id"
        labelField="label"
        allowEmpty={false}
        color="secondary"
      />
      {renderForm()}
      {attachment && attachmentType === FILE.id && (
        <AlertMessage severity="success">
          File {attachment.filename} is ready for upload
        </AlertMessage>
      )}
      <ActionButton
        data-testid="finalize-attach-button"
        variant="contained"
        color="primary"
        disabled={!canUpload()}
        onClick={handleUpload}
        processing={processing}
        startIcon={<FileUploadIcon />}
      >
        Upload
      </ActionButton>
    </Stack>
  );
}
